import React, { useId } from "react";
import { t } from "ttag";

import type { ServerCmsModelsPagesUpcomingProductsPageUpcomingProductTile as Tile } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { fallbackTileImage } from "../../utils/fallbackTileImage";
import { Clickable } from "../Clickables";
import { RichText } from "../RichText";
import { Svg } from "../Svg";

import badgeStyles from "../ProductStatusBadge/index.module.scss";
import styles from "./UpcomingProductTile.module.scss";

interface Props {
    tile: Tile;
    specSheetLinkLabel: string;
}

/**
 * One link per tile so the whole tile can delegate its click to it. The PDP
 * wins because it links to the spec sheet itself.
 */
const TileLink = ({
    tile,
    specSheetLinkLabel,
    titleId,
}: Props & { titleId: string }) => {
    if (tile.product.url) {
        return (
            <Clickable
                href={tile.product.url}
                className={styles.link}
                aria-describedby={titleId}
            >
                {t`View Product Details`}
            </Clickable>
        );
    }
    if (tile.spec_sheet_url) {
        return (
            <Clickable
                href={tile.spec_sheet_url}
                className={styles.link}
                target="_blank"
                aria-describedby={titleId}
            >
                <Svg
                    name="download"
                    aria-hidden="true"
                    className={styles.linkSvg}
                />
                {specSheetLinkLabel}
            </Clickable>
        );
    }
    return null;
};

export const UpcomingProductTile = ({ tile, specSheetLinkLabel }: Props) => {
    const { product, badge_text, body } = tile;
    const titleId = useId();

    return (
        <article className={styles.tile} onClick={delegateClickToLink}>
            <ImageChooserBlock
                value={product.image ?? fallbackTileImage}
                attrs={{ className: styles.picture }}
                imageAttrs={{ className: styles.image, alt: "" }}
                sizes={styles.sizes}
            />
            <div className={styles.content}>
                {badge_text && (
                    <p className={badgeStyles.badge}>{badge_text}</p>
                )}
                <div className={styles.details}>
                    <p className={styles.sku}>{product.sku}</p>
                    <h2 id={titleId} className={styles.title}>
                        {product.title}
                    </h2>
                    {body && <RichText value={body} className={styles.body} />}
                    {product.msrp && (
                        <p className={styles.price}>{product.msrp.formatted}</p>
                    )}
                </div>
                <TileLink
                    tile={tile}
                    specSheetLinkLabel={specSheetLinkLabel}
                    titleId={titleId}
                />
            </div>
        </article>
    );
};
